import { cn } from "@/lib/utils.ts";
import { useEffect, useRef, useState } from "react";

const defaultInputClassName =
  "size-12 rounded-md border border-input bg-background text-center text-lg font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";

/**
 * A 6-digit OTP verification code input.
 *
 * Manages its own digit state internally. Calls `onComplete` when all 6 digits
 * are filled (via typing or paste). Can be used standalone or inside a form.
 *
 * When `error` transitions to a truthy value the digits are cleared and focus
 * moves to the first input so the user can retry immediately.
 */
export function VerificationCodeInput({
  onComplete,
  disabled = false,
  error,
  onErrorClear,
  className,
  inputClassName,
}: {
  onComplete: (code: string) => void;
  disabled?: boolean;
  error?: string;
  onErrorClear?: () => void;
  /** Class for the outer wrapper (default: flex col gap-4). */
  className?: string;
  /** Class for each digit input (replaces default styling when provided). */
  inputClassName?: string;
}) {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const prevError = useRef(error);

  // When a new error appears, clear the digits and refocus.
  useEffect(() => {
    if (error && error !== prevError.current) {
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    }
    prevError.current = error;
  }, [error]);

  const submit = (digits: string) => {
    if (digits.length === 6) onComplete(digits);
  };

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    onErrorClear?.();

    // Paste full code into first input
    if (index === 0 && value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, 6);
      if (digits.length === 6) {
        setCode(digits.split(""));
        inputRefs.current[5]?.focus();
        submit(digits);
        return;
      }
    }

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newCode.every((d) => d) && newCode.join("").length === 6) {
      submit(newCode.join(""));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    onErrorClear?.();
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (digits.length === 6) {
      setCode(digits.split(""));
      inputRefs.current[5]?.focus();
      submit(digits);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="flex gap-2">
        {code.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            value={digit}
            onChange={(e) => handleCodeChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : undefined}
            disabled={disabled}
            className={inputClassName ?? defaultInputClassName}
            maxLength={index === 0 ? 6 : 1}
            autoComplete={index === 0 ? "one-time-code" : undefined}
          />
        ))}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {disabled && <p className="text-sm text-muted-foreground">Verifying...</p>}
    </div>
  );
}
